import { stopPropagation } from "@/utils"
import {
  ExternalLinkIcon,
  FileText,
  PackageSearch,
  Receipt,
  ShieldAlert,
  ShoppingCart,
  Tag,
  Store,
  Target,
} from "lucide-react"
import { useLocation } from "wouter-preact"
import {
  getAmazonPaymentRemittanceLink,
  getAmazonPoDetailLink,
  getAmazonVendorCentralLink,
} from "./amazon_utils"
import { getCvsDisputeUrl, getDisputeAction } from "./dispute_utils"
import { ListAddNoteButton } from "./notes.tsx"
import { DeductionListJson } from "@/api"
import { DrawerDeduction } from "@/dispute/file_dispute_drawer.tsx"
import { getTargetUrl, HEB_PORTAL_URL } from "@/deduction_table/util.tsx"

function getAmazonPoDetailIcon(deduction: DeductionListJson) {
  if (deduction.source.toLowerCase() !== "amazon") return null

  const poLink = getAmazonPoDetailLink(deduction)
  if (!poLink) return null

  return (
    <div
      onClick={e => {
        e.stopPropagation()
        window.open(poLink, "_blank")
      }}
      title="View PO Details"
      className="cursor-pointer">
      <ShoppingCart className="h-5 w-5 text-purple-600 cursor-pointer" />
    </div>
  )
}

function getAmazonVendorCentralIcon(deduction: DeductionListJson) {
  if (deduction.source.toLowerCase() !== "amazon") return null

  const amazonLink = getAmazonVendorCentralLink(deduction)
  if (!amazonLink) return null

  // Don't link to the disputes page - we want to use our internal dispute flow
  if (amazonLink.includes("disputes")) {
    return null
  }

  // Icon selection based on link type
  let icon = <FileText className="h-5 w-5 text-blue-600 cursor-pointer" />

  if (amazonLink.includes("coop")) {
    icon = <Tag className="h-5 w-5 text-blue-600 cursor-pointer" />
  } else if (amazonLink.includes("chargebacks")) {
    icon = <PackageSearch className="h-5 w-5 text-blue-600 cursor-pointer" />
  }

  return (
    <div
      onClick={e => {
        e.stopPropagation()
        window.open(amazonLink, "_blank")
      }}
      title="Open in Amazon Vendor Central"
      className="cursor-pointer">
      {icon}
    </div>
  )
}

function getAmazonPaymentDetailIcon(deduction: DeductionListJson) {
  if (deduction.source.toLowerCase() !== "amazon") return null

  const paymentLink = getAmazonPaymentRemittanceLink(deduction)
  if (!paymentLink) return null

  return (
    <div
      onClick={e => {
        e.stopPropagation()
        window.open(paymentLink, "_blank")
      }}
      title="View Payment Details"
      className="cursor-pointer">
      <Receipt className="h-5 w-5 text-green-600 cursor-pointer" />
    </div>
  )
}

function getDisputeActionIcon(deduction: DeductionListJson) {
  // If already disputed, show green shield
  if (deduction.dispute_id) {
    return <ShieldAlert className="h-5 w-5 text-green-500 cursor-default" />
  }

  let action = getDisputeAction(deduction, null)

  if (!action || action.type !== "can_file") {
    return null
  }

  if (deduction.source.toLowerCase() === "cvs") {
    return (
      <ShieldAlert
        onClick={() => window.open(getCvsDisputeUrl(deduction), "_blank")}
        className="h-5 w-5 text-red-600 cursor-pointer"
      />
    )
  }

  return (
    <ShieldAlert
      className="h-5 w-5 cursor-pointer text-red-600"
      onClick={() => DrawerDeduction.set(deduction)}
    />
  )
}

export function ListActionButtons({ deduction }: { deduction: DeductionListJson }) {
  const [location] = useLocation()
  const isAmazon = deduction.source.toLowerCase() === "amazon"

  if (!deduction) {
    console.log("action buttons null deduction", deduction)
  }
  const isTarget = deduction.source?.toLowerCase() === "target"
  const isHeb = deduction.source?.toLowerCase() === "heb"

  return (
    <div className="flex justify-between items-center gap-1" onClick={stopPropagation}>
      <ListAddNoteButton deduction={deduction} />
      {getDisputeActionIcon(deduction)}
      {isAmazon && getAmazonVendorCentralIcon(deduction)}
      {isAmazon && getAmazonPaymentDetailIcon(deduction)}
      {isAmazon && getAmazonPoDetailIcon(deduction)}
      <ExternalLinkIcon
        className="h-5 w-5 cursor-pointer text-black underline"
        onClick={() =>
          window.open(
            location.includes("accounting")
              ? `/accounting/transaction/${deduction.id}`
              : `/deduction/${deduction.id}`,
            "_blank"
          )
        }
      />
      {isTarget && (
        <div
          onClick={() => window.open(getTargetUrl(deduction), "_blank")}
          title="Open in Target Portal"
          className="cursor-pointer">
          <Target className="h-5 w-5 text-red-600 cursor-pointer" />
        </div>
      )}
      {isHeb && (
        <div
          onClick={() => window.open(HEB_PORTAL_URL, "_blank")}
          title="Open the H-E-B AP-AR Application — search Bills from HEB for this number to see its backup"
          className="cursor-pointer">
          <Store className="h-5 w-5 text-red-600 cursor-pointer" />
        </div>
      )}
    </div>
  )
}
